import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  Clock, 
  Users, 
  TrendingUp, 
  Zap, 
  Shield,
  CheckCircle,
  ArrowRight,
  Lock,
  Play,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette from user spec
const COLORS = {
  primary: '#1a1a2e',      // Deep navy/charcoal
  accent: '#eab308',       // Gold/amber  
  urgency: '#ef4444',      // Red for urgency
  success: '#22c55e',      // Green for positive
  text: '#f5f5f5',         // Light text
  textMuted: '#a3a3a3',    // Muted text
  cardBg: '#2d2d44',       // Card background
};

const LaunchAccessPage = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [config, setConfig] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const formRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchConfig();
    fetchActivities();
    fetchStats();
    
    // Refresh activities every 15 seconds for urgency
    const activityInterval = setInterval(fetchActivities, 15000);
    return () => clearInterval(activityInterval);
  }, []);

  // Countdown timer with seconds
  useEffect(() => {
    if (!config?.countdown_enabled || !config?.countdown_end) return;
    
    const updateCountdown = () => {
      const end = new Date(config.countdown_end);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [config]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/access/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/access/activities?limit=12`);
      const data = await response.json();
      if (data.ok) {
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/access/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formPayload = new FormData();
      formPayload.append('full_name', formData.full_name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);

      const response = await fetch(`${BACKEND_URL}/api/public/access/request`, {
        method: 'POST',
        body: formPayload
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        toast.success(`Position #${result.position_number} secured!`);
      } else {
        toast.error(result.error || 'Unable to process request');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'requested_access':
        return <Users className="h-3 w-3" />;
      case 'entered_ledger':
        return <CheckCircle className="h-3 w-3" />;
      case 'position_secured':
        return <Lock className="h-3 w-3" />;
      default:
        return <Zap className="h-3 w-3" />;
    }
  };

  const getActivityText = (type) => {
    switch (type) {
      case 'requested_access':
        return 'requested access';
      case 'entered_ledger':
        return 'entered the ledger';
      case 'position_secured':
        return 'secured position';
      default:
        return 'joined';
    }
  };

  // Success screen after submission
  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: COLORS.primary }}
      >
        <Card className="max-w-md w-full border-0 shadow-2xl" style={{ backgroundColor: COLORS.cardBg }}>
          <CardContent className="p-8 text-center">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: COLORS.success + '20' }}
            >
              <CheckCircle className="h-10 w-10" style={{ color: COLORS.success }} />
            </div>
            
            <h2 className="text-2xl font-bold mb-2" style={{ color: COLORS.text }}>
              Position Recorded
            </h2>
            
            <p className="mb-6" style={{ color: COLORS.textMuted }}>
              Your request has been logged in the system. If a position becomes available, 
              you will receive next steps via email.
            </p>
            
            <div 
              className="p-4 rounded-lg mb-6"
              style={{ backgroundColor: COLORS.primary }}
            >
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                <strong style={{ color: COLORS.accent }}>What happens next:</strong>
                <br />
                1. Review period (24-48 hours)
                <br />
                2. Invitation sent if position available
                <br />
                3. Secure your spot
              </p>
            </div>
            
            <Link to="/">
              <Button 
                className="w-full"
                style={{ 
                  backgroundColor: COLORS.accent,
                  color: COLORS.primary 
                }}
              >
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.primary }}>
      {/* SECTION 1: Hero with Urgency */}
      <section className="relative overflow-hidden">
        {/* Gradient overlay */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse at top, ${COLORS.accent}20, transparent 70%)`
          }}
        />
        
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          {/* Top badge */}
          <div className="flex justify-center mb-6">
            <Badge 
              className="px-4 py-1 text-sm font-medium animate-pulse"
              style={{ 
                backgroundColor: COLORS.urgency + '20',
                color: COLORS.urgency,
                border: `1px solid ${COLORS.urgency}40`
              }}
            >
              <Clock className="h-3 w-3 mr-2 inline" />
              Limited Access Window
            </Badge>
          </div>

          {/* Main headline */}
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-black text-center mb-6 leading-tight"
            style={{ color: COLORS.text }}
          >
            {config?.hero_headline || "This Is the Year Everything Changes."}
          </h1>
          
          <p 
            className="text-xl md:text-2xl text-center mb-10 max-w-3xl mx-auto"
            style={{ color: COLORS.textMuted }}
          >
            {config?.hero_subheadline || "But only if you move while position is still available."}
          </p>

          {/* CTA Button */}
          <div className="flex justify-center mb-12">
            <Button 
              size="lg"
              onClick={scrollToForm}
              className="text-lg px-10 py-6 rounded-full font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              style={{ 
                backgroundColor: COLORS.accent,
                color: COLORS.primary 
              }}
              data-testid="hero-cta-btn"
            >
              {config?.cta_button_text || "Request Access Now"}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>

          {/* Countdown Timer */}
          {config?.countdown_enabled && (
            <div className="flex justify-center">
              <div 
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl"
                style={{ backgroundColor: COLORS.cardBg }}
              >
                <Clock className="h-5 w-5" style={{ color: COLORS.urgency }} />
                <span className="text-sm font-medium" style={{ color: COLORS.textMuted }}>
                  {config?.countdown_label || "Access Window Closing"}:
                </span>
                <div className="flex gap-3 ml-2">
                  {[
                    { value: countdown.days, label: 'D' },
                    { value: countdown.hours, label: 'H' },
                    { value: countdown.minutes, label: 'M' },
                    { value: countdown.seconds, label: 'S' }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <span 
                        className="font-mono text-2xl font-bold"
                        style={{ color: COLORS.accent }}
                      >
                        {String(item.value).padStart(2, '0')}
                      </span>
                      <span className="text-xs block" style={{ color: COLORS.textMuted }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: Live Activity Carousel */}
      {config?.carousel_enabled && activities.length > 0 && (
        <section 
          className="py-6 overflow-hidden"
          style={{ backgroundColor: COLORS.cardBg }}
        >
          <div className="relative">
            {/* Scrolling container */}
            <div className="flex animate-scroll whitespace-nowrap">
              {/* Duplicate for seamless loop */}
              {[...activities, ...activities].map((activity, idx) => (
                <div 
                  key={idx}
                  className="inline-flex items-center gap-2 px-4 py-2 mx-2 rounded-full"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  <span style={{ color: COLORS.accent }}>
                    {getActivityIcon(activity.event_type)}
                  </span>
                  <span className="text-sm" style={{ color: COLORS.text }}>
                    <strong>{activity.display_name}</strong> {getActivityText(activity.event_type)}
                  </span>
                  <span className="text-xs" style={{ color: COLORS.textMuted }}>
                    {activity.time_ago}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          <style>{`
            @keyframes scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-scroll {
              animation: scroll 30s linear infinite;
            }
          `}</style>
        </section>
      )}

      {/* SECTION 3: Why Now - Urgency Points */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{ color: COLORS.text }}
          >
            Why People Are Moving <span style={{ color: COLORS.accent }}>Now</span>
          </h2>
          <p 
            className="text-center mb-12 max-w-2xl mx-auto"
            style={{ color: COLORS.textMuted }}
          >
            The system rewards those who act early. Here's what's at stake.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <TrendingUp className="h-8 w-8" />,
                title: "Positions Fill Fast",
                description: "Each position that fills means one less opportunity for you. Early movers get the best spots."
              },
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Locked-In Benefits",
                description: "Those who enter during launch get benefits that won't be available once we go mainstream."
              },
              {
                icon: <Zap className="h-8 w-8" />,
                title: "Compounding Effect",
                description: "Every day you wait is a day your position could be earning. Time is working against you."
              }
            ].map((item, idx) => (
              <Card 
                key={idx}
                className="border-0 transform hover:scale-105 transition-transform"
                style={{ backgroundColor: COLORS.cardBg }}
              >
                <CardContent className="p-8 text-center">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    style={{ 
                      backgroundColor: COLORS.accent + '20',
                      color: COLORS.accent 
                    }}
                  >
                    {item.icon}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ color: COLORS.text }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ color: COLORS.textMuted }}>
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Video Section (if enabled) */}
      {config?.video_enabled && config?.video_url && (
        <section 
          className="py-16"
          style={{ backgroundColor: COLORS.cardBg }}
        >
          <div className="max-w-4xl mx-auto px-4">
            <h2 
              className="text-2xl md:text-3xl font-bold text-center mb-8"
              style={{ color: COLORS.text }}
            >
              See What's <span style={{ color: COLORS.accent }}>Possible</span>
            </h2>
            
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={config.video_url} type="video/mp4" />
              </video>
              
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center opacity-80"
                  style={{ backgroundColor: COLORS.accent }}
                >
                  <Play className="h-8 w-8" style={{ color: COLORS.primary }} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 5: Social Proof Stats */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div 
            className="grid grid-cols-3 gap-4 p-8 rounded-2xl"
            style={{ backgroundColor: COLORS.cardBg }}
          >
            <div className="text-center">
              <div 
                className="text-3xl md:text-5xl font-black mb-2"
                style={{ color: COLORS.accent }}
              >
                {stats?.total_requests || 0}+
              </div>
              <div className="text-sm" style={{ color: COLORS.textMuted }}>
                People Requesting
              </div>
            </div>
            <div className="text-center border-x" style={{ borderColor: COLORS.primary }}>
              <div 
                className="text-3xl md:text-5xl font-black mb-2"
                style={{ color: COLORS.accent }}
              >
                {stats?.recent_activity_count || 0}
              </div>
              <div className="text-sm" style={{ color: COLORS.textMuted }}>
                Active Today
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-3xl md:text-5xl font-black mb-2"
                style={{ color: stats?.positions_available ? COLORS.success : COLORS.urgency }}
              >
                {stats?.positions_available ? 'OPEN' : 'FULL'}
              </div>
              <div className="text-sm" style={{ color: COLORS.textMuted }}>
                Status
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: The Form - Enter the Ledger */}
      <section className="py-16 md:py-24" ref={formRef}>
        <div className="max-w-xl mx-auto px-4">
          <Card 
            className="border-0 shadow-2xl overflow-hidden"
            style={{ backgroundColor: COLORS.cardBg }}
          >
            {/* Form header */}
            <div 
              className="p-6 text-center"
              style={{ 
                backgroundColor: COLORS.accent,
                color: COLORS.primary 
              }}
            >
              <Lock className="h-8 w-8 mx-auto mb-3" />
              <h2 className="text-2xl font-bold">
                {config?.form_title || "Enter the Ledger"}
              </h2>
              <p className="text-sm opacity-80">
                Request your position before the window closes
              </p>
            </div>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.text }}
                  >
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="Your full name"
                    className="w-full border-0"
                    style={{ 
                      backgroundColor: COLORS.primary,
                      color: COLORS.text 
                    }}
                    data-testid="access-name-input"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.text }}
                  >
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="you@example.com"
                    className="w-full border-0"
                    style={{ 
                      backgroundColor: COLORS.primary,
                      color: COLORS.text 
                    }}
                    data-testid="access-email-input"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: COLORS.text }}
                  >
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+1 (555) 123-4567"
                    className="w-full border-0"
                    style={{ 
                      backgroundColor: COLORS.primary,
                      color: COLORS.text 
                    }}
                    data-testid="access-phone-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full text-lg py-6 font-bold rounded-xl transform hover:scale-[1.02] transition-transform"
                  style={{ 
                    backgroundColor: COLORS.accent,
                    color: COLORS.primary 
                  }}
                  data-testid="access-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">
                        <Clock className="h-5 w-5" />
                      </span>
                      Processing...
                    </span>
                  ) : (
                    <>
                      {config?.cta_button_text || "Request Access Now"}
                      <ArrowRight className="h-5 w-5 ml-2 inline" />
                    </>
                  )}
                </Button>

                <p className="text-center text-xs" style={{ color: COLORS.textMuted }}>
                  By submitting, you agree to receive updates about your position.
                  <br />
                  No spam. Unsubscribe anytime.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Trust indicators */}
          <div className="flex justify-center gap-6 mt-8">
            {[
              { icon: <Shield className="h-4 w-4" />, text: "Secure Form" },
              { icon: <Lock className="h-4 w-4" />, text: "Privacy Protected" },
              { icon: <CheckCircle className="h-4 w-4" />, text: "No Obligation" }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2"
                style={{ color: COLORS.textMuted }}
              >
                <span style={{ color: COLORS.accent }}>{item.icon}</span>
                <span className="text-xs">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: Final CTA */}
      <section 
        className="py-16 text-center"
        style={{ backgroundColor: COLORS.cardBg }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <h2 
            className="text-2xl md:text-4xl font-bold mb-4"
            style={{ color: COLORS.text }}
          >
            The Only Question Is:
            <span className="block" style={{ color: COLORS.accent }}>
              Will You Be In Position?
            </span>
          </h2>
          <p 
            className="mb-8"
            style={{ color: COLORS.textMuted }}
          >
            Those who hesitate often watch others succeed. 
            Request your position now while access is still available.
          </p>
          <Button
            size="lg"
            onClick={scrollToForm}
            className="px-10 py-6 text-lg font-bold rounded-full shadow-lg"
            style={{ 
              backgroundColor: COLORS.accent,
              color: COLORS.primary 
            }}
            data-testid="final-cta-btn"
          >
            Secure My Position
            <ArrowRight className="h-5 w-5 ml-2 inline" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center" style={{ color: COLORS.textMuted }}>
        <p className="text-sm">
          &copy; {new Date().getFullYear()} BBC Quick List. All rights reserved.
        </p>
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <Link to="/terms" className="hover:underline">Terms</Link>
          <Link to="/privacy-policy" className="hover:underline">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default LaunchAccessPage;
